import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import type { BoardDoc } from '../doc/BoardDoc';
import type { Item } from '../doc/schema';
import type { Role } from '../sync/boardsApi';
import type { SyncState } from '../sync/HttpProvider';
import type { PaneState, PaneStore } from './paneStore';

export const BoardContext = createContext<BoardDoc | null>(null);
export const PaneContext = createContext<PaneStore | null>(null);

export interface BoardMeta {
  title: string;
  role: Role | null;
}
export const BoardMetaContext = createContext<BoardMeta>({ title: '', role: null });

export function useBoard(): BoardDoc {
  const board = useContext(BoardContext);
  if (!board) throw new Error('useBoard outside BoardContext');
  return board;
}

export const useBoardMeta = (): BoardMeta => useContext(BoardMetaContext);

/** True when the current visitor may only look at this board (viewer role). */
export const useReadOnly = (): boolean => useContext(BoardMetaContext).role === 'viewer';



export function usePaneStore(): PaneStore {
  const store = useContext(PaneContext);
  if (!store) throw new Error('usePaneStore outside PaneContext');
  return store;
}

export function usePane<T>(selector: (s: PaneState) => T): T {
  return useStore(usePaneStore(), selector);
}

/** Subscribes to one item. Returns the same object until that item changes. */
export function useItem(id: string): Item | undefined {
  const { index } = useBoard();
  return useSyncExternalStore(index.subscribe, () => index.get(id));
}

/** Re-renders on any document change. Use sparingly (inspector, status bar). */
export function useItemsVersion(): number {
  const { index } = useBoard();
  return useSyncExternalStore(index.subscribe, index.getVersion);
}

/** The parts of a Y.Map this hook needs; structural so maps of any value type fit. */
interface ObservableMap {
  observeDeep(f: () => void): void;
  unobserveDeep(f: () => void): void;
  toJSON(): unknown;
}

/** Plain snapshot of a Y.Map (deep), refreshed on any change inside it. Stable reference between changes. */
export function useYMap<T>(map: ObservableMap): Record<string, T> {
  const cache = useRef<{ version: number; value: Record<string, T> }>({ version: -1, value: {} });
  const versionRef = useRef(0);
  const subscribe = useCallback(
    (cb: () => void) => {
      const handler = () => {
        versionRef.current++;
        cb();
      };
      map.observeDeep(handler);
      return () => map.unobserveDeep(handler);
    },
    [map],
  );
  const getSnapshot = useCallback(() => {
    if (cache.current.version !== versionRef.current) {
      cache.current = { version: versionRef.current, value: map.toJSON() as Record<string, T> };
    }
    return cache.current.value;
  }, [map]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

const LOCAL_SYNC: SyncState = { status: 'synced', error: null, role: 'owner', lastSeq: 0, queued: 0 };
const noop = () => () => {};

/** Sync state of the board's provider; a constant "synced" for local boards. */
export function useSyncState(): SyncState {
  const { provider } = useBoard();
  return useSyncExternalStore(
    provider ? provider.store.subscribe : noop,
    provider ? provider.store.getState : () => LOCAL_SYNC,
  );
}
